<template>
    <div class="spotlight-root">
        <Header :label="fullName" overlap="small" />
        <UserNavSection />
        <MainLayout
            marginTop
            :class="{ disabled: loadingBar }"
            hideOnMobile="right"
        >
            <ProfileModule slot="right" dense />
            <FeaturedResource slot="right" />
            <VerifyModule slot="right" />

            <ProfileModule slot="mobTop" dense />
            <PersonalDetails
                id="personal"
                ref="personalDetails"
                slot="center"
                v-model="user"
                :isLoading="isLoadingData"
                @image:changed="
                    img => {
                        changedImage = img;
                    }
                "
                @image:removed="
                    () => {
                        changedImage = null;
                    }
                "
                @removeOldImage="
                    () => {
                        user.image = null;
                    }
                "
            />
            <ContactDetails
                id="contact"
                v-show="true"
                slot="center"
                v-model="user"
                :oldUserData="oldUserData()"
                :isLoading="isLoadingData"
            />

            <Languages
                id="language"
                slot="center"
                v-model="candidateData.languages"
                :oldLanguage="oldCandidateData().languages"
                :isLoading="isLoadingData"
            />
            <Education
                id="education"
                slot="center"
                v-model="candidateData.degrees"
                :oldDegrees="oldCandidateData().degrees"
                v-on:change="handleChange"
                :isLoading="isLoadingData"
            />

            <Communication
                slot="center"
                v-model="user.newsLetterConsent"
                :isLoading="isLoadingData"
            />
        </MainLayout>

        <transition name="slide-in-out">
            <div v-if="showSaveButton" class="footer">
                <div style="height: 20px">
                    <LoadingBar
                        v-if="loadingBar"
                        class="loadingbar"
                        :speed="loadingSpeed"
                        :target="loadingBar"
                        @hitmax="handleLoadingFinish"
                        animated
                    />
                </div>
                <div class="footer-info">
                    <div class="align-self-center" style="flex: 2;">
                        <h6
                            class="mb-0 pr-3"
                            style="text-align: right; line-height: 33px;"
                        >
                            {{ $t('spotlight.saveChangesToSubmit') }}
                        </h6>
                    </div>
                    <div style="flex: 1;">
                        <BaseButton
                            variant="secondary"
                            class="px-5 py-1"
                            @click="onSave"
                        >
                            <div
                                class="d-flex align-items-center justify-content-center"
                            >
                                <BaseIcon name="save" class="mr-1" size="lg" />
                                {{ $t('spotlight.saveChanges') }}
                            </div>
                        </BaseButton>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
// Project components
import Header from '~/components/layouts/Header';
import MainLayout from '~/components/layouts/mainlayout/MainLayout';
import UserNavSection from '~/shared/UserNavSection';
import ProfileModule from '~/components/smart/ProfileModule/ProfileModule';
import FeaturedResource from '~/components/smart/FeaturedResource';
import LoadingBar from '~/shared/LoadingBar';
import VerifyModule from '~/components/miscellaneous/VerifyModule';

// Page Components
import PersonalDetails from './components/PersonalDetails';
import ContactDetails from './components/ContactDetails';
import Languages from './components/Languages';
import Education from './components/Education';
import Communication from './components/Communication';
import UnsavedChangesModal from './components/UnsavedChangesModal';

// Utils
import CandidateService from '~/services/CandidateService';
import IntercomService from '~/services/IntercomService';
import DialogService from '~/services/DialogService';
import MessageService from '~/services/MessageService';
import asyncForEach from '~/utils/asyncForEach';
import deepClone from '~/utils/deepClone';
import findDeletedDegrees from '~/utils/degrees/findDeletedDegrees';
import hasDegreeChanged from '~/utils/degrees/hasDegreeChanged';
import hasUserChanged from '~/utils/user/hasUserChanged';
import userDataToName from '~/utils/user/userDataToName';
import spotlightErrorMapper from '~/utils/errors/spotlightErrorMapper';
import { extractErrorResponse } from '~/utils/errors/authErrorMapper';
import { emailValidator } from '~/utils/validators';

export default {
    components: {
        Header,
        MainLayout,
        UserNavSection,
        ProfileModule,
        VerifyModule,
        PersonalDetails,
        FeaturedResource,
        ContactDetails,
        Languages,
        Education,
        Communication,
        LoadingBar,
        UnsavedChangesModal,
    },
    name: 'Spotlight',
    data() {
        return {
            forceCandidateUpdate: false,
            forceUserUpdate: false,
            user: {}, // The unsaved user data (firstName, nationality ... etc)
            candidateData: {}, // The unsaved candidate data (degrees, language ... etc )
            showSaveButton: false,
            loadingBar: 0,
            loadingSpeed: 1,
            profileId: null,
            changedImage: null, // The image to save
        };
    },
    computed: {
        shouldCreateModal() {
            return this.$interfaces.unsavedChanges.isOpen();
        },
        candidate() {
            const temp = this.forceCandidateUpdate;
            this.forceCandidateUpdate = false;
            return this.$interfaces.candidate.me.get({
                reactive: true,
                forceUpdate: temp,
            });
        },
        userData() {
            const temp = this.forceUserUpdate;
            this.forceUserUpdate = false;
            return this.$interfaces.user.me.get({
                reactive: true,
                forceUpdate: temp,
            });
        },
        loading() {
            return this.candidate.loading;
        },
        userLoading() {
            return this.userData.loading;
        },
        isLoading() {
            return this.loading || this.userLoading;
        },
        isLoadingData() {
            // Returns true if is loading and no data has been fetched yet.
            // This is only for when the page is loading for the first time.
            return this.isLoading && (!this.candidateData.id || !this.user.id);
        },
        fullName() {
            let data = this.userData;
            data = data ? data.data : {};
            return userDataToName(data, 'spotlight.header');
        },
    },
    methods: {
        incrementLoading() {
            this.loadingBar += (100 - this.loadingBar) / 2;
        },
        oldCandidateData() {
            return this.candidate.data || {};
        },
        oldUserData() {
            return this.userData.data || {};
        },
        replaceCandidateData() {
            this.candidateData = deepClone(this.candidate.data) || {};
            if (this.candidateData instanceof Array) {
                this.candidateData = {};
            }
        },
        replaceUserData() {
            this.user = deepClone(this.userData.data);
            if (this.user instanceof Array) {
                this.user = {};
            }
        },
        handleLoadingFinish() {
            setTimeout(() => {
                this.loadingBar = 0;
                this.showSaveButton = false;
            }, 1000);
        },
        hasDataChanged(newData, oldData) {
            const a = JSON.stringify(newData);
            const b = JSON.stringify(oldData);
            return a !== b;
        },
        handleChange() {
            this.showSaveButton = true;
        },
        createOnSaveMessage() {
            MessageService.newMessage(
                this.$t('general.success').concat('!'),
                this.$t('spotlight.saveSuccessful')
            );
        },
        async onSave() {
            this.loadingBar = 20;
            const candidateData = this.candidateData || {};
            const userData = this.user || {};
            const oldUserData = this.userData;

            const errors = [];

            // User data
            const newUserData = {
                firstName: userData.firstName,
                lastName: userData.lastName,
                nationality: userData.nationality,
                phoneNumber: userData.phoneNumber,
                newsLetterConsent: userData.newsLetterConsent,
            };
            // If email is not set, validate email, and add it to the update request
            if (
                userData.email &&
                !Boolean(oldUserData.email) &&
                emailValidator(userData.email)
            ) {
                newUserData.email = userData.email;
            }
            if (userData.image === null) {
                newUserData.avatar = '';
            }

            // Save user data
            await CandidateService.saveUserData(newUserData).catch(
                responseError => {
                    errors.push(...(extractErrorResponse(responseError) || []));
                }
            );

            // User image - if image was changed upload it
            if (this.changedImage) {
                await CandidateService.saveUserImage(this.changedImage).catch(
                    responseErrors => errors.push(...responseErrors)
                );
                this.changedImage = null;
                this.$refs.personalDetails.resetChangedImage();
            }

            // Save degrees
            const oldCandidateData = this.oldCandidateData();
            await CandidateService.saveDegrees(
                candidateData.degrees,
                oldCandidateData.degrees
            ).catch(responseErrors => {
                errors.push(...responseErrors);
            });

            // Save languages
            await CandidateService.saveLanguages(
                candidateData.languages,
                oldCandidateData.languages
            ).catch(responseErrors => {
                errors.push(...responseErrors);
            });

            // Sets loading bar to 100%
            this.loadingBar = 100;
            this.loadingSpeed = 100;

            // Reloads the saved results
            this.forceCandidateUpdate = true;
            this.forceUserUpdate = true;

            // Creates a successful-save message
            if (errors.length > 0) {
                // Show error messages
                MessageService.errorWithMapper(
                    null,
                    errors[0],
                    spotlightErrorMapper,
                    spotlightErrorMapper
                );
            } else {
                // Show succcess message
                this.createOnSaveMessage();
            }

            IntercomService.setUserData();
            IntercomService.update();
        },
    },
    async mounted() {
        this.replaceUserData();
        const scrollTo = this.$route.query.scrollTo;
        if (scrollTo) {
            this.$scrollTo(scrollTo);
        }
        await this.$interfaces.user.me.get();
        await this.$interfaces.candidate.me.get().then(data => {
            this.candidateData = deepClone(data.data);
        });
    },
    watch: {
        loading(isLoading, wasLoading) {
            if (!isLoading && wasLoading) {
                this.replaceCandidateData();
            }
        },
        userLoading(isLoading, wasLoading) {
            if (!isLoading && wasLoading) {
                this.replaceUserData();
            }
        },
        candidateData: {
            deep: true,
            handler() {
                this.showSaveButton =
                    this.hasDataChanged(
                        this.candidateData,
                        this.oldCandidateData()
                    ) ||
                    /*  this.hasDataChanged(this.user, this.oldUserData()) || */
                    this.loadingBar;
            },
        },
        user: {
            deep: true,
            handler() {
                this.showSaveButton =
                    /* this.hasDataChanged(this.candidate, this.oldCandidateData()) || */
                    this.hasDataChanged(this.user, this.oldUserData()) ||
                    this.loadingBar;
            },
        },
        showSaveButton(shouldShow, wasShown) {
            if (shouldShow !== wasShown) {
                this.$interfaces.unsavedChanges.setHasUnsavedChanges(
                    shouldShow
                );
            }
        },
        changedImage(val) {
            this.showSaveButton = Boolean(val) || this.loadingBar;
        },
        shouldCreateModal(shouldShow, wasShown) {
            if (shouldShow && !wasShown) {
                DialogService.openSpotlightExitDialog(
                    this.onSave,
                    this.$router
                );
            }
        },
    },
};
</script>

<style scoped>
.spotlight-root {
    padding-bottom: 100px;
}

.footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0px;
    height: 80px;
    background-color: white;
    transition: bottom 2s;
    box-shadow: 0 -2px 5px var(--light-gray);

    display: flex;
    flex-direction: column;
}
.footer .footer-info {
    display: flex;
}

@media only screen and (max-width: 600px) {
    .footer {
        background-color: transparent;
        box-shadow: none;
        justify-content: flex-end;
    }
    .footer-info > *:first-child {
        display: none;
    }
    button {
        width: 90%;
        margin: 0 5%;
        margin-bottom: 5px;
        text-align: center !important;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15), 0 2px 3px rgba(0, 0, 0, 0.2);
    }
}

.slide-in-out-enter-active,
.slide-in-out-leave-active {
    transition: bottom 0.7s;
}
.slide-in-out-enter,
.slide-in-out-leave-to {
    bottom: -100px;
}

.loadingbar {
    height: 6px;
}
</style>
